"use client";

import { useState } from "react";
import { primaryButtonClass } from "@/components/form";
import { ProgressBar } from "@/components/ProgressBar";
import { ToolShell, type ToolResult } from "@/components/ToolShell";
import { useToolFiles } from "@/hooks/useToolFiles";
import { converterUrl } from "@/lib/converter";
import { runOnServer, serverProgressLabel, type ServerProgress } from "@/lib/server-client";
import type { Tool } from "@/lib/tools";

/** The page for a server tool (Phase 5): its settings, a button, upload and
 *  conversion progress, plain-language errors, then ToolShell's Download step. */
export function ServerTool({
  tool,
  job,
  options,
  ready = true,
  button,
  result,
  children
}: {
  tool: Tool;
  /** Converter job, e.g. "compress". */
  job: string;
  /** Settings sent with the file (or worked out from it). */
  options: Record<string, unknown> | ((file: File) => Record<string, unknown>);
  /** False while the settings aren't complete (e.g. passwords differ). */
  ready?: boolean;
  button: string;
  /** Names and describes the converted file. */
  result: (file: File, blob: Blob, note: string | null) => ToolResult;
  /** The tool's settings. */
  children?: React.ReactNode;
}) {
  const {
    files,
    setFiles,
    notices
  } = useToolFiles(tool);
  const file = files[0];
  const [progress, setProgress] = useState<ServerProgress | null>(null);
  const [results, setResults] = useState<ToolResult[] | null>(null);
  const [error, setError] = useState("");
  function change(next: File[]) {
    setFiles(next);
    setResults(null);
    setError("");
  }
  async function start() {
    if (!file) return;
    setError("");
    try {
      const {
        blob,
        note
      } = await runOnServer(job, file, typeof options === "function" ? options(file) : options, setProgress);
      setResults([result(file, blob, note)]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setProgress(null);
    }
  }
  return <ToolShell tool={tool} files={files} onFilesChange={change} notices={notices} results={results} onStartOver={() => change([])}>
      <section aria-label={tool.name} className="flex flex-col gap-4 rounded-[16px] border bg-card p-4 sm:p-5">
        {children}
        {error && <p role="alert" className="text-sm font-medium text-destructive">
            {error}
          </p>}
        {progress ? <ProgressBar label={serverProgressLabel(progress)} value={progress.phase === "uploading" ? Math.round((progress.fraction ?? 0) * 100) : undefined} max={progress.phase === "uploading" ? 100 : undefined} /> : <button type="button" onClick={start} disabled={!file || !ready || !converterUrl} className={primaryButtonClass}>
            {button}
          </button>}
      </section>
    </ToolShell>;
}